// AI endpoints — natural-language search and post generation.
//
// Wire shapes derive from the backend OpenAPI codegen
// (`components["schemas"]["ExtractFiltersRequest"]` etc.).

import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";
import type { components } from "@/types/api";

// ---------------------------------------------------------------------------
// /ai/extract-filters
// ---------------------------------------------------------------------------

/**
 * Backend's filter draft schema. Aliased so call sites use the local name
 * without leaking the codegen path.
 */
export type AiExtractedFilters = components["schemas"]["PropertyListQueryDraft"];

export type AiExtractFiltersRequest = components["schemas"]["ExtractFiltersRequest"];
export type AiExtractFiltersResponse = components["schemas"]["ExtractFiltersResponse"];

function postExtractFilters(
  body: AiExtractFiltersRequest,
): Promise<AiExtractFiltersResponse> {
  return apiRequest<AiExtractFiltersResponse, AiExtractFiltersRequest>(
    "/ai/extract-filters",
    { method: "POST", body },
  );
}

export function useExtractFilters() {
  return useMutation({
    mutationFn: (message: string) => postExtractFilters({ message }),
  });
}

// ---------------------------------------------------------------------------
// /ai/generate-post
// ---------------------------------------------------------------------------

// Frontend-only: tone is typed as `string` on the backend (the AI
// orchestrator passes it through verbatim). The frontend narrows to the
// three values the UI surface offers so we don't accidentally submit a
// freeform string.
export type GeneratePostTone = "professional" | "casual" | "luxurious";

export type AiGeneratePostRequest = components["schemas"]["GeneratePostRequest"];
export type AiGeneratePostResponse = components["schemas"]["GeneratePostResponse"];

function postGeneratePost(
  body: AiGeneratePostRequest,
): Promise<AiGeneratePostResponse> {
  return apiRequest<AiGeneratePostResponse, AiGeneratePostRequest>(
    "/ai/generate-post",
    { method: "POST", body },
  );
}

export function useGeneratePost() {
  return useMutation({
    mutationFn: (body: AiGeneratePostRequest) => postGeneratePost(body),
  });
}
